import { absoluteUrl, findStaticRoute, SITE_NAME, SITE_URL, STATIC_ROUTES } from "./seoConfig";

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
  element.dataset.seoManaged = "true";
  return element;
}

function removeMeta(attribute, key) {
  document.head.querySelectorAll(`meta[${attribute}="${key}"]`).forEach((element) => element.remove());
}

function upsertCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", url);
  element.dataset.seoManaged = "true";
}

function removeCanonical() {
  document.head.querySelectorAll('link[rel="canonical"]').forEach((element) => element.remove());
}

function replaceStructuredData(items = []) {
  document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((element) => element.remove());
  document.head.querySelectorAll('#eternua-animaux-main-jsonld, #eternua-animaux-breadcrumb-jsonld').forEach((element) => element.remove());
  items.filter(Boolean).forEach((item, index) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seoJsonld = "true";
    script.dataset.seoIndex = String(index);
    script.textContent = JSON.stringify(item).replace(/</g, "\\u003c");
    document.head.appendChild(script);
  });
}

function breadcrumbData(path, title, lang) {
  if (path === "/") return null;
  const segments = path.split("/").filter(Boolean);
  const elements = [{ "@type": "ListItem", position: 1, name: SITE_NAME, item: `${SITE_URL}/` }];
  let current = "";
  segments.forEach((segment, index) => {
    current += `/${segment}`;
    const route = findStaticRoute(current, lang);
    elements.push({
      "@type": "ListItem",
      position: index + 2,
      name: index === segments.length - 1 ? title.replace(/\s*\|.*$/, "") : route?.title?.replace(/\s*\|.*$/, "") || segment.replace(/-/g, " "),
      item: absoluteUrl(current),
    });
  });
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: elements };
}

function baseStructuredData(meta) {
  const items = [];
  if (meta.path === "/" && meta.indexable) {
    items.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      logo: absoluteUrl("/logo512-optimized.png"),
    });
    items.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      inLanguage: meta.lang || "fr",
    });
  }
  if (meta.indexable) items.push(breadcrumbData(meta.path, meta.title, meta.lang || "fr"));
  if (meta.path === "/market" && meta.indexable) {
    const universes = STATIC_ROUTES.filter((route) => route.section === "universes" && route.indexable !== false);
    items.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: meta.lang === "fr" ? "Univers Eternua" : "Eternua worlds",
      itemListElement: universes.map((route, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: findStaticRoute(route.path, meta.lang || "fr").title.replace(/\s*\|.*$/, ""),
        url: absoluteUrl(route.path),
      })),
    });
  }
  return items;
}

export function applySeoMetadata(meta) {
  if (typeof document === "undefined") return;
  const canonical = meta.canonical || absoluteUrl(meta.path || "/");
  const robots = meta.robots || (meta.indexable ? "index, follow, max-image-preview:large" : "noindex, follow");

  const lang = meta.lang || document.documentElement.lang || "fr";
  document.documentElement.lang = lang;
  document.title = meta.title;
  upsertMeta("name", "description", meta.description);
  upsertMeta("name", "robots", robots);
  upsertMeta("name", "googlebot", robots);
  if (meta.sensitive || meta.notFound || meta.canonical === null || meta.canonical === "") {
    removeCanonical();
  } else {
    upsertCanonical(canonical);
  }

  if (meta.shareable) {
    const image = absoluteUrl(meta.image || "/images/social/eternua-home.jpg");
    upsertMeta("property", "og:type", meta.ogType || "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", lang === "fr" ? "fr_FR" : lang === "en" ? "en_US" : lang);
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", meta.imageAlt || SITE_NAME);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", meta.title);
    upsertMeta("name", "twitter:description", meta.description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:image:alt", meta.imageAlt || SITE_NAME);
  } else {
    ["og:type", "og:site_name", "og:locale", "og:title", "og:description", "og:url", "og:image", "og:image:width", "og:image:height", "og:image:alt"].forEach((key) => removeMeta("property", key));
    ["twitter:card", "twitter:title", "twitter:description", "twitter:image", "twitter:image:alt"].forEach((key) => removeMeta("name", key));
  }

  replaceStructuredData(meta.structuredData || baseStructuredData({ ...meta, lang }));
}
